import React, { useState } from "react";
import { DraftCreateDialogProps, CreateDraftData } from "./types";
import { useVersioningStore } from "@/stores/versioning/versioning-store";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogClose,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { X, Plus, FileText, GitBranch, Info, AlertCircle } from "lucide-react";

export const DraftCreateDialog: React.FC<DraftCreateDialogProps> = ({
  open,
  onOpenChange,
  onCreateDraft,
  baseVersion,
  branchId,
}) => {
  const { versions, branches, currentVersion } = useVersioningStore();
  const [formData, setFormData] = useState<CreateDraftData>({
    name: "",
    description: "",
    baseVersion: baseVersion || currentVersion || 1,
    branchId: branchId,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (
    field: keyof CreateDraftData,
    value: string | number,
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = "Draft name is required";
    } else if (formData.name.length > 100) {
      newErrors.name = "Draft name must be less than 100 characters";
    }

    if (formData.description && formData.description.length > 500) {
      newErrors.description = "Description must be less than 500 characters";
    }

    if (!formData.baseVersion) {
      newErrors.baseVersion = "Base version is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsSubmitting(true);

    try {
      await onCreateDraft(formData);

      // Reset form
      setFormData({
        name: "",
        description: "",
        baseVersion: currentVersion || 1,
        branchId: undefined,
      });
      setErrors({});
    } catch (error) {
      // Error handling is done in the parent component
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      onOpenChange(false);
      setErrors({});
    }
  };

  const selectedVersion = versions.find(
    (v) => v.version === formData.baseVersion,
  );
  const selectedBranch = formData.branchId
    ? branches.find((b) => b.id === formData.branchId)
    : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle className="flex items-center space-x-2">
              <Plus className="w-5 h-5" />
              <span>Create New Draft</span>
            </DialogTitle>

            <DialogClose asChild>
              <Button variant="outline" size="sm" disabled={isSubmitting}>
                <X className="w-4 h-4" />
              </Button>
            </DialogClose>
          </div>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Draft Name */}
          <div>
            <Label htmlFor="draft-name">
              Name <span className="text-destructive">*</span>
            </Label>
            <Input
              id="draft-name"
              value={formData.name}
              onChange={(e) => handleInputChange("name", e.target.value)}
              placeholder="Enter draft name..."
              className={errors.name ? "border-destructive" : ""}
              disabled={isSubmitting}
              autoFocus
            />
            {errors.name && (
              <p className="text-sm text-destructive mt-1 flex items-center space-x-1">
                <AlertCircle className="w-3 h-3" />
                <span>{errors.name}</span>
              </p>
            )}
          </div>

          {/* Description */}
          <div>
            <Label htmlFor="draft-description">Description</Label>
            <Textarea
              id="draft-description"
              value={formData.description}
              onChange={(e) => handleInputChange("description", e.target.value)}
              placeholder="Optional description of what this draft contains..."
              className={errors.description ? "border-destructive" : ""}
              disabled={isSubmitting}
              rows={3}
            />
            {errors.description && (
              <p className="text-sm text-destructive mt-1 flex items-center space-x-1">
                <AlertCircle className="w-3 h-3" />
                <span>{errors.description}</span>
              </p>
            )}
          </div>

          {/* Base Version */}
          <div>
            <Label htmlFor="base-version">
              Base Version <span className="text-destructive">*</span>
            </Label>
            <Select
              value={formData.baseVersion.toString()}
              onValueChange={(value) =>
                handleInputChange("baseVersion", parseInt(value))
              }
              disabled={isSubmitting}
            >
              <SelectTrigger
                className={errors.baseVersion ? "border-destructive" : ""}
              >
                <SelectValue placeholder="Select base version" />
              </SelectTrigger>
              <SelectContent>
                {[...versions]
                  .sort((a, b) => b.version - a.version)
                  .map((version) => (
                    <SelectItem
                      key={version.version}
                      value={version.version.toString()}
                    >
                      <div className="flex items-center space-x-2">
                        <span>v{version.version}</span>
                        {version.name && (
                          <span className="text-muted-foreground">
                            - {version.name}
                          </span>
                        )}
                        {version.version === currentVersion && (
                          <Badge variant="secondary" className="text-xs">
                            Current
                          </Badge>
                        )}
                      </div>
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
            {errors.baseVersion && (
              <p className="text-sm text-destructive mt-1 flex items-center space-x-1">
                <AlertCircle className="w-3 h-3" />
                <span>{errors.baseVersion}</span>
              </p>
            )}
          </div>

          {/* Branch (Optional) */}
          {branches.length > 0 && (
            <div>
              <Label htmlFor="branch-select">Branch (Optional)</Label>
              <Select
                value={formData.branchId || ""}
                onValueChange={(value) =>
                  handleInputChange("branchId", value || undefined)
                }
                disabled={isSubmitting}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select branch (optional)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="">No branch</SelectItem>
                  {branches
                    .filter((branch) => branch.status === "active")
                    .map((branch) => (
                      <SelectItem key={branch.id} value={branch.id}>
                        <div className="flex items-center space-x-2">
                          <GitBranch className="w-3 h-3" />
                          <span>{branch.name}</span>
                          {branch.description && (
                            <span className="text-muted-foreground text-sm">
                              - {branch.description}
                            </span>
                          )}
                        </div>
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Selected Information */}
          <div className="bg-muted/50 rounded-lg p-3 space-y-2">
            <div className="flex items-center space-x-2 text-sm">
              <Info className="w-4 h-4 text-muted-foreground" />
              <span className="font-medium">Draft will be created from:</span>
            </div>

            <div className="space-y-1 text-sm text-muted-foreground">
              <div className="flex items-center space-x-2">
                <FileText className="w-3 h-3" />
                <span>Version {formData.baseVersion}</span>
                {selectedVersion?.name && <span>- {selectedVersion.name}</span>}
              </div>

              {selectedBranch && (
                <div className="flex items-center space-x-2">
                  <GitBranch className="w-3 h-3" />
                  <span>Branch: {selectedBranch.name}</span>
                </div>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end space-x-2 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>

            <Button
              type="submit"
              disabled={isSubmitting || !formData.name.trim()}
              className="min-w-24"
            >
              {isSubmitting ? (
                <div className="flex items-center space-x-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Creating...</span>
                </div>
              ) : (
                <>
                  <Plus className="w-4 h-4 mr-2" />
                  Create Draft
                </>
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default DraftCreateDialog;
